import { useState, useRef } from "react";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaWhatsapp,
  FaClock,
  FaPaperPlane,
} from "react-icons/fa";
import emailjs from "@emailjs/browser";

function Contact() {
  const form = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    emailjs
      .sendForm(
        "service_bx521fr",
        "template_p83s5e7",
        form.current,
        "MXW7UywVRSUgvDXyh"
      )
      .then(
        () => {
          alert("Message sent successfully!");
          form.current.reset();
          setIsSubmitting(false);
        },
        (error) => {
          console.error("EmailJS Error:", error);
          alert("Failed to send message. Please try again.");
          setIsSubmitting(false);
        }
      );
  };

  return (
    <div className="bg-slate-50 font-sans text-slate-800">
      {/* Hero Section */}
      <section className="relative bg-[#0B0F19] text-white py-28 overflow-hidden">
        {/* Fine-lined Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)`,
            backgroundSize: '32px 32px'
          }}
        ></div>
        
        {/* Glow accent effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight">
            Contact Us
          </h1>

          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto font-light leading-relaxed">
            Providing high-precision Rebar Detailing, Shop Drawings, Bar Bending Schedules (BBS), BIM Modeling and MEP BIM Coordination alongside our associates across the GCC and global markets.
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">

          {/* Quick Contact Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">

            {/* Call Us Card */}
            <div className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-md transition-all duration-300 border border-slate-100 flex flex-col items-center text-center group">
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mb-5 text-slate-800 group-hover:bg-slate-900 group-hover:text-white transition duration-300">
                <FaPhoneAlt className="text-lg" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Call Us
              </h3>
              <p className="text-slate-600 font-medium">
                +971 56 914 0526
              </p>
            </div>

            {/* Email Us Card */}
            <div className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-md transition-all duration-300 border border-slate-100 flex flex-col items-center text-center group">
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mb-5 text-slate-800 group-hover:bg-slate-900 group-hover:text-white transition duration-300">
                <FaEnvelope className="text-lg" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Email Us
              </h3>
              <p className="text-slate-600 font-medium text-sm break-all">
                info@vamcouae.com
              </p>
            </div>

            {/* Location Card */}
            <div className="bg-white p-8 rounded-3xl shadow-sm hover:shadow-md transition-all duration-300 border border-slate-100 flex flex-col items-center text-center group">
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mb-5 text-slate-800 group-hover:bg-slate-900 group-hover:text-white transition duration-300">
                <FaMapMarkerAlt className="text-lg" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Location
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed font-medium">
                Deira Clock Tower, Dubai, UAE
              </p>
            </div>

          </div>

          {/* Contact Form + Info Panel Section */}
          <div className="grid lg:grid-cols-12 gap-10 items-start">

            {/* Contact Form Column */}
            <div className="lg:col-span-7 bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-slate-100">
              <h2 className="text-3xl font-extrabold text-slate-900 mb-2 tracking-tight">
                Send Us A Message
              </h2>
              <p className="text-slate-500 text-sm mb-8">
                Fill in the form below and our engineering team will respond within 24 hours.
              </p>

              <form ref={form} onSubmit={sendEmail} className="space-y-6">
                <div>
                  <label className="block text-xs uppercase font-bold text-slate-500 mb-2 tracking-wider">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="user_name"
                    required
                    placeholder="John Doe"
                    className="w-full border border-slate-200 bg-slate-50/50 p-4 rounded-2xl text-sm focus:outline-none focus:bg-white focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs uppercase font-bold text-slate-500 mb-2 tracking-wider">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="user_email"
                      required
                      placeholder="name@company.com"
                      className="w-full border border-slate-200 bg-slate-50/50 p-4 rounded-2xl text-sm focus:outline-none focus:bg-white focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs uppercase font-bold text-slate-500 mb-2 tracking-wider">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      name="user_phone"
                      placeholder="+971 -- --- ----"
                      className="w-full border border-slate-200 bg-slate-50/50 p-4 rounded-2xl text-sm focus:outline-none focus:bg-white focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase font-bold text-slate-500 mb-2 tracking-wider">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    placeholder="e.g., Construction LLC"
                    className="w-full border border-slate-200 bg-slate-50/50 p-4 rounded-2xl text-sm focus:outline-none focus:bg-white focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase font-bold text-slate-500 mb-2 tracking-wider">
                    Required Engineering Service *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows="4"
                    placeholder="Describe your design layouts, tonnage estimates, or project timeline requirements..."
                    className="w-full border border-slate-200 bg-slate-50/50 p-4 rounded-2xl text-sm focus:outline-none focus:bg-white focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition resize-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-slate-900 hover:bg-slate-800 active:scale-[0.99] text-white font-semibold py-4 rounded-2xl shadow-lg shadow-slate-900/10 transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      <span>Send Message</span>
                      <FaPaperPlane className="text-xs" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Sidebar Details Column */}
            <div className="lg:col-span-5 space-y-8">
              {/* Dark Office Detail Card */}
              <div className="bg-[#0B0F19] text-white p-8 md:p-10 rounded-3xl relative overflow-hidden shadow-xl">
                <div 
                  className="absolute inset-0 opacity-[0.04] pointer-events-none"
                  style={{
                    backgroundImage: `linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)`,
                    backgroundSize: '24px 24px'
                  }}
                ></div>

                <h2 className="text-2xl font-bold mb-8 tracking-tight relative z-10 border-b border-slate-800 pb-4">
                  Get In Touch
                </h2>

                <div className="space-y-6 relative z-10">
                  <div className="flex gap-4 items-start">
                    <div className="p-3 bg-slate-800/80 rounded-xl text-slate-300">
                      <FaPhoneAlt />
                    </div>
                    <div>
                      <h4 className="font-semibold text-xs text-slate-400 uppercase tracking-wider mb-1">
                        Phone Lines
                      </h4>
                      <p className="text-slate-200 text-sm space-y-1 font-medium">
                        <span className="block"><strong className="text-slate-400 font-normal">Landline:</strong> +971 4 547 9597</span>
                        <span className="block"><strong className="text-slate-400 font-normal">Mobile:</strong> +971 56 914 0526</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="p-3 bg-slate-800/80 rounded-xl text-slate-300">
                      <FaEnvelope />
                    </div>
                    <div>
                      <h4 className="font-semibold text-xs text-slate-400 uppercase tracking-wider mb-1">
                        Official Inquiries
                      </h4>
                      <p className="text-slate-200 text-sm font-medium">
                        info@vamcouae.com
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="p-3 bg-slate-800/80 rounded-xl text-slate-300">
                      <FaMapMarkerAlt />
                    </div>
                    <div>
                      <h4 className="font-semibold text-xs text-slate-400 uppercase tracking-wider mb-1">
                        Contact Address
                      </h4>
                      <p className="text-slate-200 text-sm font-medium leading-relaxed">
                        Deira Clock Tower, Dubai, United Arab Emirates
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="p-3 bg-slate-800/80 rounded-xl text-slate-300">
                      <FaClock />
                    </div>
                    <div>
                      <h4 className="font-semibold text-xs text-slate-400 uppercase tracking-wider mb-1">
                        Working Hours
                      </h4>
                      <p className="text-slate-200 text-sm font-medium">
                        Monday - Saturday
                      </p>
                      <p className="text-slate-400 text-xs mt-0.5">
                        8:00 AM - 6:00 PM (GST)
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* WhatsApp Support Box */}
              <div className="bg-emerald-600 text-white p-8 rounded-3xl text-center shadow-lg shadow-emerald-600/15">
                <FaWhatsapp className="text-5xl mx-auto mb-3 opacity-95" />

                <h3 className="text-2xl font-bold mb-2 tracking-tight">
                  WhatsApp Support
                </h3>

                <p className="mb-6 text-emerald-100 font-light text-sm max-w-sm mx-auto leading-relaxed">
                  Connect instantly with our engineering detailing managers for real-time project turnaround support.
                </p>

                <a
                  href="https://wa.me/971569140526"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-white text-emerald-800 hover:bg-emerald-50 px-8 py-3.5 rounded-xl font-bold inline-block transition text-sm shadow-sm active:scale-95"
                >
                  Chat Now
                </a>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Map Section */}
      <section className="h-[450px] w-full bg-slate-100 border-t border-slate-200">
        <iframe
          title="VAMCO HQ Office Location Map"
          src="https://www.google.com/maps?q=Deira+Clock+Tower,+Dubai&output=embed"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          className="grayscale opacity-90 contrast-125 focus:outline-none"
        />
      </section>
    </div>
  );
}

export default Contact;